const DEFAULT_GA4_MEASUREMENT_ID = 'G-43KTYPJPNM';
const CHECKOUT_EVENT_NAMES = new Set(['begin_checkout', 'add_payment_info']);
const PAYMENT_TYPES = new Set(['card', 'bit', 'wire', 'cheque']);
const EPISODE_ID_PATTERN = /^ce_[A-Za-z0-9_-]{8,77}$/;
const ITEM_TEXT_PATTERN = /^[^\u0000-\u001f\u007f]{1,100}$/;

function cleanItemText(value) {
  const normalized = String(value ?? '').trim();
  return ITEM_TEXT_PATTERN.test(normalized) ? normalized : '';
}

function normalizeNonNegativeNumber(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0
    ? Number(numeric.toFixed(2))
    : 0;
}

function normalizeNonNegativeInteger(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric >= 0 ? numeric : 0;
}

function sanitizeItems(items) {
  if (!Array.isArray(items)) return [];
  const sanitized = [];

  for (const item of items.slice(0, 500)) {
    const itemId = cleanItemText(item?.item_id);
    const itemName = cleanItemText(item?.item_name);
    const itemVariant = cleanItemText(item?.item_variant);
    const quantity = Number(item?.quantity);
    if ((!itemId && !itemName) || !Number.isSafeInteger(quantity) || quantity <= 0) continue;

    const next = { quantity };
    if (itemId) next.item_id = itemId;
    if (itemName) next.item_name = itemName;
    if (itemVariant) next.item_variant = itemVariant;
    sanitized.push(next);
  }

  return sanitized;
}

function resolveMeasurementId(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return /^G-[A-Z0-9]+$/.test(normalized)
    ? normalized
    : DEFAULT_GA4_MEASUREMENT_ID;
}

export function normalizeCheckoutEpisodeId(value) {
  const normalized = String(value || '').trim();
  return EPISODE_ID_PATTERN.test(normalized) ? normalized : '';
}

export function createCheckoutEpisodeId({
  nowMs = Date.now(),
  randomId,
} = {}) {
  const numericNow = Number(nowMs);
  const timestamp = Number.isFinite(numericNow) && numericNow >= 0
    ? Math.trunc(numericNow).toString(36)
    : Date.now().toString(36);
  let entropy = String(randomId || '').trim();

  if (!entropy) {
    try {
      entropy = globalThis.crypto?.randomUUID?.() || '';
    } catch (_) {
      entropy = '';
    }
  }
  if (!entropy) {
    entropy = Math.random().toString(36).slice(2, 18);
  }

  const safeEntropy = entropy.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 48);
  const episodeId = normalizeCheckoutEpisodeId(`ce_${timestamp}_${safeEntropy}`);
  if (episodeId) return episodeId;

  return `ce_${timestamp}_fallback`;
}

export function resolveCheckoutEpisodeId(existingValue, options = {}) {
  const existing = normalizeCheckoutEpisodeId(existingValue);
  if (existing) {
    return { episodeId: existing, created: false };
  }
  return {
    episodeId: createCheckoutEpisodeId(options),
    created: true,
  };
}

export function buildCheckoutTelemetryPayload({
  eventName,
  episodeId,
  value,
  cartItemCount,
  cartUnitCount,
  paymentType,
  items,
  measurementId,
} = {}) {
  const normalizedEventName = String(eventName || '').trim();
  const normalizedEpisodeId = normalizeCheckoutEpisodeId(episodeId);
  if (!CHECKOUT_EVENT_NAMES.has(normalizedEventName) || !normalizedEpisodeId) {
    return null;
  }

  const payload = {
    checkout_episode_id: normalizedEpisodeId,
    checkout_stage: normalizedEventName === 'begin_checkout' ? 'open' : 'confirm',
    checkout_value_scope: 'merchandise_after_discount_before_delivery_vat',
    currency: 'ILS',
    value: normalizeNonNegativeNumber(value),
    cart_item_count: normalizeNonNegativeInteger(cartItemCount),
    cart_unit_count: normalizeNonNegativeInteger(cartUnitCount),
    items: sanitizeItems(items),
    send_to: resolveMeasurementId(measurementId),
  };

  if (normalizedEventName === 'add_payment_info') {
    const normalizedPaymentType = String(paymentType || '').trim().toLowerCase();
    if (!PAYMENT_TYPES.has(normalizedPaymentType)) return null;
    payload.payment_type = normalizedPaymentType;
  }

  return payload;
}

export function trackGa4CheckoutEvent({
  gtag,
  ...options
} = {}) {
  const payload = buildCheckoutTelemetryPayload(options);
  if (!payload) return { sent: false, reason: 'invalid_checkout_telemetry' };

  const sender = typeof gtag === 'function'
    ? gtag
    : (typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null);
  if (!sender) return { sent: false, reason: 'gtag_unavailable', payload };

  try {
    sender('event', options.eventName, payload);
    return { sent: true, payload };
  } catch (_) {
    return { sent: false, reason: 'gtag_exception', payload };
  }
}
